/**
 * TagChip — atom for a team-defined tag.
 *
 * Single click → callback (apply to whole team).
 * Double click → callback (open selective dialog).
 *
 * The single/double-click discriminator lives in useTags.handleTagClick.
 * This component just forwards the click events.
 */

import React from 'react';
import { ButtonBase, Box, Typography } from '@mui/material';
import { useStatsTheme, withAlpha } from '../theme/statsTheme';

export default function TagChip({
  tag,
  onClick,
  onDoubleClick,
  size = 'md',
  active = false,
  count,
}) {
  const theme = useStatsTheme();

  const color = (tag && tag.color) || theme.actionColors.tag;
  const heights = { sm: 28, md: 34, lg: 40 };
  const fontSizes = { sm: 11, md: 12, lg: 14 };

  return (
    <ButtonBase
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      focusRipple
      sx={{
        position: 'relative',
        height: heights[size],
        px: 1.5,
        borderRadius: theme.radius.pill,
        backgroundColor: active ? withAlpha(color, 0.42) : withAlpha(color, 0.16),
        border: `1px solid ${withAlpha(color, active ? 0.7 : 0.36)}`,
        color: theme.text.primary,
        fontFamily: theme.typography.fontFamily,
        whiteSpace: 'nowrap',
        transition: `background-color ${theme.motion.fast}, transform ${theme.motion.fast}`,
        '&:hover': {
          backgroundColor: withAlpha(color, 0.3),
          transform: 'translateY(-1px)',
        },
        '&:active': {
          transform: 'scale(0.96)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: color,
            boxShadow: `0 0 6px ${withAlpha(color, 0.7)}`,
          }}
        />
        <Typography
          component="span"
          sx={{
            fontSize: fontSizes[size],
            fontWeight: 600,
            letterSpacing: '0.02em',
            color: theme.text.primary,
          }}
        >
          {tag?.libelle || ''}
        </Typography>
        {typeof count === 'number' && count > 0 && (
          <Typography
            component="span"
            sx={{
              fontSize: 10,
              fontWeight: 700,
              ml: 0.4,
              px: 0.6,
              borderRadius: theme.radius.sm,
              backgroundColor: withAlpha('#000000', 0.4),
              color: theme.text.secondary,
            }}
          >
            ×{count}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
}
